import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { 
  Facebook, 
  MessageCircle, 
  MessageSquare,
  Instagram,
  Link as LinkIcon,
  Check,
  Share2
} from 'lucide-react';

const ShareProduct = ({ product, trigger }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Generate product URL
  const productUrl = `${window.location.origin}/product/${product.slug || product.id}`;
  const productTitle = product.name || t('shareProduct.defaultTitle');
  const productDescription = product.description || '';
  const productImage = product.images?.[0]?.large || product.images?.[0]?.medium || '';

  // Share handlers
  const shareViaFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    setOpen(false);
  };

  const shareViaWhatsApp = () => {
    const text = `${productTitle}\n${productUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    setOpen(false);
  };

  const shareViaSMS = () => {
    const text = `${productTitle}\n${productUrl}`;
    window.location.href = `sms:?body=${encodeURIComponent(text)}`;
    setOpen(false);
  };

  const shareViaInstagram = () => {
    // Instagram doesn't support direct URL sharing via web
    // Copy link and show instruction
    copyLink();
    toast({
      title: t('shareProduct.linkCopied'),
      description: t('shareProduct.instagramHint'),
    });
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(productUrl);
      setCopied(true);
      toast({
        title: t('shareProduct.linkCopied'),
        description: t('shareProduct.linkCopiedDescription'),
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: t('shareProduct.copyFailed'),
        description: t('shareProduct.tryAgain'),
        variant: 'destructive'
      });
    }
  };

  // Native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: productTitle,
          text: productDescription,
          url: productUrl,
        });
        setOpen(false);
      } catch (error) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    } else {
      // Fallback to dialog
      setOpen(true);
    }
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      onClick: shareViaWhatsApp,
      color: 'hover:bg-green-50 hover:text-green-600'
    },
    {
      name: 'SMS',
      icon: MessageSquare,
      onClick: shareViaSMS,
      color: 'hover:bg-blue-50 hover:text-blue-600'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      onClick: shareViaFacebook,
      color: 'hover:bg-blue-50 hover:text-blue-700'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      onClick: shareViaInstagram,
      color: 'hover:bg-pink-50 hover:text-pink-600'
    },
  ];

  return (
    <>
      {/* Trigger Button */}
      <div onClick={() => navigator.share ? handleNativeShare() : setOpen(true)}>
        {trigger || (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Share2 className="w-4 h-4" />
            {t('shareProduct.share')}
          </Button>
        )}
      </div>

      {/* Share Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('shareProduct.title')}</DialogTitle>
            <DialogDescription>
              {t('shareProduct.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Product Preview */}
            <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
              {productImage && (
                <img
                  src={productImage}
                  alt={productTitle}
                  className="w-16 h-16 object-cover rounded"
                />
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm line-clamp-2">{productTitle}</h4>
                {product.price && (
                  <p className="text-sm text-[#be1f1a] font-semibold mt-1">
                    ${product.price}
                  </p>
                )}
              </div>
            </div>

            {/* Share Options */}
            <div className="grid grid-cols-4 gap-3">
              {shareOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.name}
                    onClick={option.onClick}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${option.color}`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{option.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Copy Link */}
            <div className="pt-2 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={productUrl}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border rounded-lg bg-gray-50 text-gray-600"
                />
                <Button
                  onClick={copyLink}
                  variant="outline"
                  className={`gap-2 ${copied ? 'bg-green-50 text-green-600 border-green-200' : ''}`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('shareProduct.copied')}
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4" />
                      {t('shareProduct.copy')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareProduct;
